import { translatedClientError } from "../../i18n/client-error.js";
import { apiErrorText } from "../../api/api-error.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Contact lists"` — the sentences the screen says, as pure functions, so every
 * one of them is provable without mounting anything (customer-portal review 2026-09-25, the
 * Contact lists section: "CL #104"–"CL #140").
 *
 * ── REFUSALS ARE THE API'S WORDS ─────────────────────────────────────────────────────────
 * The contact-list routes word every refusal for the person who chose the file — the line, the
 * limit, what to change (CL #105, CL #106) — and name the field it is about. The screen shows that
 * message beside that field ({@link refusalOf}), and never replaces it with a line that says only
 * "could not be saved". A failure that is not the API's (the network) is shown as what it is.
 */
import { ApiError } from "@wetalk/api-client";
import {
  CONTACT_LIST_NAME_MAXIMUM,
  CONTACT_LIST_NAME_TAKEN_MESSAGE,
  contactListNameProblem,
  isNameTaken,
} from "@wetalk/domain";

import type {
  ContactColumnRoleView,
  ContactColumnView,
  ContactListPersonView,
  ContactListRowView,
  ContactListUnreadableRowView,
  ContactListUnrecognisedValueView,
} from "../../api/gateway.js";

const COUNT_FORMAT = () => new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE"));

/** `1,840`. Every count on this screen is grouped, because four digits without it misread. */
export function countText(count: number): string {
  return COUNT_FORMAT().format(count);
}

export function plural(count: number, singular: string, many: string): string {
  return count === 1 ? singular : many;
}

export function peopleText(count: number): string {
  return `${countText(count)} ${plural(count, portalText("operations", "person_d39a4750"), portalText("operations", "people_ab3ccc8d"))}`;
}

/**
 * The longest list name the API accepts (CL #130), on create and on rename: the design's 40 since plan-015 NAM (G18).
 * A name saved before the rule is kept until it is renamed.
 */
export const NAME_MAX = CONTACT_LIST_NAME_MAXIMUM;

/** The API's bounds on the two rules (CL #122). */
export const WITHIN_DAY_MAX = 3650;
export const AFTER_NO_ANSWER_MAX = 100;

/** The design's defaults: 30 days on, three no-answers off. */
export const DEFAULT_WITHIN_DAY = 30;
export const DEFAULT_AFTER_NO_ANSWER = 3;

// -------------------------------------------------------------------------------------------
// The page
// -------------------------------------------------------------------------------------------

/**
 * "3 lists · 20,024 people · 41 on do not call". The people figure is the API's count of
 * different numbers across the lists (CL #124), so a number on two lists is one person.
 */
export function kickerText(listCount: number, personCount: number, suppressedCount: number): string {
  return (
    portalText("operations", "value_value_value_value_on_do_not_call_a4298d11", { v0: String(countText(listCount)), v1: String(plural(listCount, portalText("operations", "list_38b62be4"), portalText("operations", "lists_da2ea5be"))), v2: String(peopleText(personCount)), v3: String(countText(suppressedCount)) })
  );
}

/** The two rules as a card states them (CL #122). */
export function rulesText(row: Pick<ContactListRowView, "skipCalledWithinDay" | "skipAfterNoAnswer">): string {
  const part: string[] = [];
  if (row.skipCalledWithinDay !== null) {
    part.push(
      portalText("operations", "skips_people_called_in_the_last_value_value_86d51716", { v0: String(countText(row.skipCalledWithinDay)), v1: String(plural(row.skipCalledWithinDay, portalText("operations", "day_a2620cbc"), portalText("operations", "days"))) }),
    );
  }
  if (row.skipAfterNoAnswer !== null) {
    part.push(
      portalText("operations", "stops_after_value_unanswered_value_08438b82", { v0: String(countText(row.skipAfterNoAnswer)), v1: String(plural(row.skipAfterNoAnswer, portalText("operations", "call_bc8d8647"), portalText("operations", "calls_51b6cb25"))) }),
    );
  }
  const joined = part.length === 0 ? portalText("operations", "calls_everyone_not_on_do_not_call_12ef9b14") : part.join(portalText("operations", "opening.period.and"));
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/** "5 rows skipped" on a card, or `null` when there is nothing to say (CL #120). */
export function skippedText(skippedRowCount: number | null): string | null {
  if (skippedRowCount === null || skippedRowCount === 0) return null;
  return portalText("operations", "contacts.skippedRows", { count: skippedRowCount, v0: countText(skippedRowCount) });
}

/**
 * A column name the API made up because the source had no header row — `column_1` for a CSV
 * whose first line was already a person (CL #108). It means nothing to the member (CL #126).
 */
export function isSynthesisedColumnName(columnName: string): boolean {
  return /^column_\d+$/.test(columnName);
}

/**
 * The column line on a card. A paste has no columns worth naming (CL #126), and neither does a
 * made-up `column_1`, so it says nothing rather than "columns: column_1".
 */
export function columnLineText(row: Pick<ContactListRowView, "source" | "columnName">): string | null {
  if (row.source === "paste") return null;
  const named = row.columnName.filter((one) => !isSynthesisedColumnName(one));
  if (named.length === 0) return null;
  return portalText("operations", "columns_value_aa7c6680", { v0: String(named.join(", ")) });
}

// -------------------------------------------------------------------------------------------
// Removing (CL #131, CL #137, CL #139)
// -------------------------------------------------------------------------------------------

/** The confirmation's title: which list, how many people, when it came in (CL #137). */
export function removeTitleText(row: Pick<ContactListRowView, "name">): string {
  return portalText("operations", "remove_value_c397d768", { v0: String(row.name) });
}

export function removeBodyText(
  row: Pick<ContactListRowView, "personCount" | "arrivalText" | "source">,
): string {
  const what = row.source === "csv_upload" ? portalText("operations", "the_file_you_uploaded_ba0ba2f7") : portalText("operations", "the_numbers_you_pasted_464a0a99");
  return (
    portalText("operations", "this_list_has_value_and_was_value_the_people_on_it_and_val_ab47a4eb", { v0: String(peopleText(row.personCount)), v1: String(row.arrivalText), v2: String(what) })
  );
}

/** Why Remove is not offered: a campaign keeps the list it called (CL #131). */
export function removeBlockedText(campaignCount: number): string {
  return (
    portalText("operations", "this_list_is_used_by_value_so_it_cannot_be_removed_a_campa_58b0c294", { v0: String(campaignCount === 1 ? portalText("operations", "a_campaign_2c6d18cd") : portalText("operations", "value_campaigns_fa6dfa53", { v0: String(countText(campaignCount)) })) })
  );
}

export function removedText(name: readonly string[]): string {
  if (name.length === 1) return portalText("operations", "removed_value_69fb05d9", { v0: String(name[0] ?? "") });
  return portalText("operations", "removed_value_lists_6efa5008", { v0: String(countText(name.length)) });
}

export function removeSeveralTitleText(count: number): string {
  return portalText("operations", "remove_value_value_5a7d1060", { v0: String(countText(count)), v1: String(plural(count, portalText("operations", "list_38b62be4"), portalText("operations", "lists_da2ea5be"))) });
}

// -------------------------------------------------------------------------------------------
// The new-list form
// -------------------------------------------------------------------------------------------

/**
 * Whether a role's value may be said aloud, by role. A `Record` over every role (a3b9010's
 * `SPOKEN_BY_ROLE`, kept by the merge), so adding a role to `ContactColumnRoleView` does not
 * compile until someone decides here whether its value may be spoken.
 */
export const SPOKEN_BY_ROLE: Readonly<Record<ContactColumnRoleView, boolean>> = {
  display_name: true,
  personal_detail: true,
  e164: false,
  language_code: false,
  time_zone: false,
  ignored: false,
};

/**
 * Whether a role's value may be said aloud (CL #107). Follows the chosen role, never the first
 * proposal: a phone number, a language or a time zone is never spoken; a name or a personal
 * detail may be; an ignored column is not stored at all.
 */
export function spokenFor(role: ContactColumnRoleView): boolean {
  return SPOKEN_BY_ROLE[role];
}

/** "3 of 8 people will be saved — 5 skipped" (CL #120). */
export function readableSummaryText(readableCount: number, rowCount: number): string {
  const skipped = rowCount - readableCount;
  const head = portalText("operations", "value_of_value_will_be_saved_ffe19fa5", { v0: String(countText(readableCount)), v1: String(peopleText(rowCount)) });
  return skipped <= 0 ? head : portalText("operations", "value_value_skipped_e7f6bfc4", { v0: String(head), v1: String(countText(skipped)) });
}

/** What the inspection found in a source with no people (CL #115). */
export function emptySourceText(source: "csv_upload" | "paste"): string {
  return source === "paste"
    ? portalText("operations", "nothing_that_looks_like_a_person_was_found_in_what_you_pas_d7bc78cd")
    : portalText("operations", "this_file_has_a_header_row_but_no_people_under_it_2f458b05");
}

/**
 * The "Time zone" cell of a list's people (2026-10-10): the zone campaigns call them in; for a number that spans zones
 * without saying which, how many it could be in (calls wait until every one is inside the hours); else none.
 */
export function personZoneText(one: Pick<ContactListPersonView, "timeZone" | "possibleTimeZone">): string {
  if (one.timeZone !== null) return one.timeZone;
  if (one.possibleTimeZone.length > 0) {
    return portalText("operations", "one_of_value_zones_called_only_when_all_are_inside_the_hou_d13e2f90", { v0: String(String(one.possibleTimeZone.length)) });
  }
  return portalText("operations", "none_skipped_by_campaigns_36318382");
}

export function unrecognisedText(one: ContactListUnrecognisedValueView): string {
  return one.kind === "unknown_time_zone"
    ? portalText("operations", "line_value_value_is_not_a_time_zone_wetalk_knows_fb69cc8c", { v0: String(String(one.line)), v1: String(one.value) })
    : portalText("operations", "line_value_value_is_not_a_language_code_ade2deb8", { v0: String(String(one.line)), v1: String(one.value) });
}

// AMENDED 2026-10-09 (plan-015 NAM, G18): `duplicateNameText` (CL #123 — "Saving makes a second one with the same
// name; "Basic (2)" would tell them apart.") is gone. A list name is now unique in the workspace (the API refuses a
// second one with 409 name_taken) and "(" is outside the design's character set, so both its halves were untrue.

// -------------------------------------------------------------------------------------------
// Changing who is on a saved list, and downloading it (fix round R, CL #121)
// -------------------------------------------------------------------------------------------

/**
 * "Added 12 people. 2 were already on the list. 1 row was skipped." / "Replaced 1,839 people with
 * 12. 1 row was skipped." — what adding or replacing did, from the API's counts.
 */
export function peopleChangedText(
  kind: "add" | "replace",
  change: {
    readonly addedCount: number;
    readonly duplicateCount: number;
    readonly removedCount: number;
    readonly unreadableRow: readonly unknown[];
  },
): string {
  const head =
    kind === "add"
      ? portalText("operations", "added_value_9cb70827", { v0: String(peopleText(change.addedCount)) })
      : portalText("operations", "replaced_value_with_value_682ecb0f", { v0: String(peopleText(change.removedCount)), v1: String(countText(change.addedCount)) });
  const duplicate =
    change.duplicateCount === 0
      ? ""
      : portalText("operations", "contacts.duplicate", { count: change.duplicateCount, v0: countText(change.duplicateCount) });
  const otherSkipped = change.unreadableRow.length - change.duplicateCount;
  const skipped =
    otherSkipped <= 0 ? "" : portalText("operations", "contacts.skipped", { count: otherSkipped, v0: countText(otherSkipped) });
  return `${head}${duplicate}${skipped}`;
}

/** The download's name, as the API names it: `contact-list-autumn-customers-2026-09-25.csv`. */
export function contactListFileName(name: string, day: string): string {
  const slug = name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug === "" ? `contact-list-${day}.csv` : `contact-list-${slug}-${day}.csv`;
}

/**
 * The sample file behind "Download a sample CSV" (review #54; the Claude Design export of 2026-10-10): the four
 * columns of the list's format, with the rows the export's paste placeholder shows.
 */
export const SAMPLE_CSV =
  "phone,name,language,note\n" +
  "+39 06 555 0142,Giulia Rossi,Italian,ordered last Friday\n" +
  "+39 06 555 0177,Marco Bellini,English,\n" +
  "+39 333 555 0190,,,\n";

export const SAMPLE_CSV_FILE_NAME = "contact-list-sample.csv";

// -------------------------------------------------------------------------------------------
// Refusals
// -------------------------------------------------------------------------------------------

/** Where a refusal is shown: beside the name, the source, the columns, or at the foot. */
export type RefusalPlace = "name" | "source" | "column" | "other";

export interface Refusal {
  readonly place: RefusalPlace;
  readonly message: string;
}

/**
 * A refusal, placed beside the field the API named and worded as the API worded it (CL #105,
 * CL #106). An error that is not the API's is shown as its own message.
 */
export function refusalOf(error: Error): Refusal {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return { place: "other", get message() { return portalText("operations", "too_many_requests_in_a_short_time_wait_a_minute_and_try_ag_7bce66a4"); } };
    }
    if (error.status >= 500) {
      return {
        place: "other",
        get message() { return portalText("operations", "wetalk_could_not_answer_just_now_so_nothing_was_saved_try_607d49ba"); },
      };
    }
    const place: RefusalPlace =
      error.field === "name"
        ? "name"
        : error.field === "column"
          ? "column"
          : error.field === "source_text" || error.field === "source"
            ? "source"
            : "other";
    return { place, message: apiErrorText(error, "") };
  }
  return { place: "other", message: apiErrorText(error, "") };
}

/** A 4xx is the source's own answer: asking again would get the same one (CL #127). */
export function retryInspection(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 1;
}

// -------------------------------------------------------------------------------------------
// The design export of 2026-10-09
// -------------------------------------------------------------------------------------------

/** The design's bound on a new list's name: "0/40" — the API's own since plan-015 NAM (G18). */
export const NEW_LIST_NAME_MAX = CONTACT_LIST_NAME_MAXIMUM;

export interface ListNameCheck {
  /** A problem, "Looks good.", or nothing while it is empty. */
  readonly message: string;
  readonly invalid: boolean;
}

/**
 * The design's rule for a list's name, checked as it is typed. `existing` is the names in use. Since plan-015 NAM
 * (G18) this is the API's own rule and key (`contactListNameProblem`, `nameKeyOf` in `@wetalk/domain`), so the field
 * says "Looks good." only to a name the API accepts.
 */
export function listNameCheck(raw: string, existing: readonly string[]): ListNameCheck {
  if (raw === "") return { message: "", invalid: false };
  const problem = contactListNameProblem(raw) ?? (isNameTaken(raw, existing) ? CONTACT_LIST_NAME_TAKEN_MESSAGE : null);
  return problem === null ? { get message() { return portalText("operations", "looks_good_6e842722"); }, invalid: false } : { get message() { return translatedClientError(problem) ?? problem; }, invalid: true };
}

// -------------------------------------------------------------------------------------------
// The list's format (the Claude Design export of 2026-10-10)
// -------------------------------------------------------------------------------------------

/**
 * The columns a list may contain, in the export's order and words: the first row names them with exactly these
 * names, only `phone` is required, any other column is ignored. They replace the column-role mapping the member
 * used to choose (the export draws a read-only "Columns found" instead), so the console sends the API the mapping
 * these names give ({@link fixedColumnsOf}); the API's own proposal is not used.
 */
export const FORMAT_COLUMN: readonly {
  readonly name: string;
  readonly role: ContactColumnRoleView;
  /** The tag in "Columns found". */
  readonly use: string;
  /** The line in "What the list can contain". */
  readonly help: string;
}[] = [
  { name: "phone", role: "e164", get use() { return portalText("shell", "merge.contacts.phoneUse"); }, get help() { return portalText("shell", "merge.contacts.phoneHelp"); } },
  { name: "name", role: "display_name", get use() { return portalText("operations", "name_709a2322"); }, get help() { return portalText("shell", "merge.contacts.nameHelp"); } },
  {
    name: "language",
    role: "language_code",
    get use() { return portalText("operations", "language_89b86ab0"); },
    get help() { return portalText("shell", "merge.contacts.languageHelp"); },
  },
  {
    name: "note",
    role: "personal_detail",
    get use() { return portalText("shell", "merge.contacts.note"); },
    get help() { return portalText("shell", "merge.contacts.noteHelp"); },
  },
];

/**
 * A `time_zone` column. The export no longer names it, but a list that says where its people are keeps working
 * (2026-10-10, zone-from-number): it is read silently — not offered in the format, shown as what it is when found.
 */
export const TIME_ZONE_COLUMN = { name: "time_zone", role: "time_zone", get use() { return portalText("shell", "merge.contacts.zone"); } } as const;

/** A column's name as compared: trimmed, lower-cased, without surrounding quotes. */
export function columnKeyOf(sourceName: string): string {
  return sourceName.trim().replace(/^"(.*)"$/u, "$1").trim().toLowerCase();
}

/** The format's entry for a column name, or `null` for a name the list does not read. */
function formatEntryOf(sourceName: string): { readonly role: ContactColumnRoleView; readonly use: string } | null {
  const key = columnKeyOf(sourceName);
  if (key === TIME_ZONE_COLUMN.name) return TIME_ZONE_COLUMN;
  return FORMAT_COLUMN.find((one) => one.name === key) ?? null;
}

/**
 * The mapping the format gives the columns the API found: each named column takes its role — the first of two with
 * one name, the other is ignored, as a header row is read left to right — and every other column is ignored.
 * Whether a value may be spoken follows the role ({@link spokenFor}).
 */
export function fixedColumnsOf(found: readonly ContactColumnView[]): readonly ContactColumnView[] {
  const taken = new Set<ContactColumnRoleView>();
  return found.map((one) => {
    const entry = formatEntryOf(one.sourceName);
    const role: ContactColumnRoleView = entry === null || taken.has(entry.role) ? "ignored" : entry.role;
    if (role !== "ignored") taken.add(role);
    return { ...one, role, spoken: spokenFor(role) };
  });
}

/** One row of "Columns found": the name as written, an example and its tag. */
export interface FoundColumn {
  readonly name: string;
  readonly sample: string | null;
  readonly use: string;
  readonly read: boolean;
}

export function foundColumnsOf(column: readonly ContactColumnView[]): readonly FoundColumn[] {
  return column.map((one) => {
    const use =
      one.role === "ignored" ? portalText("shell", "merge.contacts.ignored") : (formatEntryOf(one.sourceName)?.use ?? portalText("shell", "merge.contacts.ignored"));
    return { name: one.sourceName, sample: one.sample[0] ?? null, use, read: one.role !== "ignored" };
  });
}

function quotedList(name: readonly string[]): string {
  const quoted = name.map((one) => `"${one}"`);
  if (quoted.length <= 1) return quoted.join("");
  return `${quoted.slice(0, -1).join(", ")} ${portalLocale() === "el" ? "και" : "and"} ${quoted[quoted.length - 1] ?? ""}`;
}

/** The sentence under "Columns found": whether the list can be called, and which columns are ignored. */
export function columnsNoteOf(column: readonly ContactColumnView[]): { readonly text: string; readonly problem: boolean } {
  if (!column.some((one) => one.role === "e164")) {
    return {
      get text() { return portalText("shell", "merge.contacts.noPhone"); },
      problem: true,
    };
  }
  const ignored = column.filter((one) => one.role === "ignored").map((one) => one.sourceName);
  if (ignored.length === 0) return { text: portalText("shell", "merge.contacts.ready"), problem: false };
  return {
    text:
      ignored.length === 1
        ? portalText("shell", "merge.contacts.ignoredOne", { names: quotedList(ignored) })
        : portalText("shell", "merge.contacts.ignoredMany", { names: quotedList(ignored) }),
    problem: false,
  };
}

/** One line of the paste check: its line number and what to fix. */
export interface PasteLine {
  readonly line: number;
  readonly why: string;
}

/**
 * The paste's first row, checked as the export checks it: it must name the columns, with `phone` among them; a
 * name the list does not read is said once (the first). `null` while nothing is typed. The line is the first one
 * with something on it.
 */
export function pasteHeaderOf(text: string): { readonly line: PasteLine | null; readonly named: boolean } | null {
  const lines = text.split(/\r\n|\r|\n/u);
  const at = lines.findIndex((one) => one.trim() !== "");
  if (at < 0) return null;
  const head = (lines[at] ?? "").split(/[,;\t]/u).map(columnKeyOf);
  if (!head.includes("phone")) {
    return {
      named: false,
      line: { line: at + 1, get why() { return portalText("shell", "merge.contacts.headerInstruction"); } },
    };
  }
  const unknown = head.find((one) => one !== "" && formatEntryOf(one) === null);
  return {
    named: true,
    line:
      unknown === undefined
        ? null
        : { line: at + 1, why: portalText("shell", "merge.contacts.unknownColumn", { name: unknown }) },
  };
}

/** What the paste check says about a row the API could not read, in the export's words where it has them. */
export function pasteRowWhyOf(
  row: Pick<ContactListUnreadableRowView, "line" | "code" | "problem">,
  unrecognised: readonly ContactListUnrecognisedValueView[],
): string {
  switch (row.code) {
    case "missing_number":
      return portalText("shell", "merge.contacts.noPhoneRow");
    case "duplicate_number":
      return portalText("shell", "merge.contacts.duplicateRow");
    case "unknown_language": {
      const value = unrecognised.find((one) => one.line === row.line && one.kind === "unknown_language")?.value ?? "";
      return /^[a-z]{3}$/iu.test(value)
        ? portalText("shell", "merge.contacts.unknownCode", { value })
        : portalText("shell", "merge.contacts.unknownLanguageName", { value: value.slice(0, 20) });
    }
    case "invalid_number":
    case "too_many_fields":
      return `${row.problem.charAt(0).toUpperCase()}${row.problem.slice(1)}.`;
  }
}

/** "Skip people called recently": the design's seven windows, as days for the API. */
export const RECENT_WINDOW: readonly { readonly day: number; readonly label: string; readonly phrase: string }[] = [
  { day: 1, get label() { return portalText("operations", "1_day_2d8a6f7c"); }, get phrase() { return portalText("operations", "yesterday_or_today_622d70df"); } },
  { day: 7, get label() { return portalText("operations", "1_week_fdb8d5cd"); }, get phrase() { return portalText("operations", "in_the_last_week_45b94031"); } },
  { day: 14, get label() { return portalText("operations", "2_weeks_8c8660be"); }, get phrase() { return portalText("operations", "in_the_last_two_weeks_91e8f6af"); } },
  { day: 30, get label() { return portalText("operations", "1_month_42fa50c1"); }, get phrase() { return portalText("operations", "in_the_last_month_b8f7154a"); } },
  { day: 90, get label() { return portalText("operations", "3_months_b9e4e6ff"); }, get phrase() { return portalText("operations", "in_the_last_three_months_dbfde4f3"); } },
  { day: 180, get label() { return portalText("operations", "6_months_cd75b86f"); }, get phrase() { return portalText("operations", "in_the_last_six_months_858474c5"); } },
  { day: 365, get label() { return portalText("operations", "1_year_afe36da6"); }, get phrase() { return portalText("operations", "in_the_last_year_524f8a45"); } },
];

export function recentRuleText(on: boolean, day: number): string {
  if (!on) return portalText("operations", "off_people_can_be_called_again_however_recently_they_were_0b8c7daa");
  const window = RECENT_WINDOW.find((one) => one.day === day);
  return portalText("operations", "anyone_this_workspace_called_value_is_left_out_9dc333e2", { v0: String(window === undefined ? portalText("operations", "in_the_last_value_days_3611e721", { v0: String(countText(day)) }) : window.phrase) });
}

/** The design's "Skip numbers with no answer three times": a fixed three. */
export const NO_ANSWER_LIMIT = 3;

/** The tag on a list card, its glyph and its tooltip (design export 2026-10-09). */
export function listStateOf(row: Pick<ContactListRowView, "state" | "campaignCount">): {
  readonly label: string;
  readonly tip: string;
  readonly calling: boolean;
} {
  switch (row.state) {
    case "in_use":
      return {
        label: portalText("operations", "in_value_value_f2347724", { v0: String(countText(row.campaignCount)), v1: String(plural(row.campaignCount, portalText("operations", "campaign_23f43ae9"), portalText("operations", "campaigns_8897ac40"))) }),
        tip:
          row.campaignCount === 1
            ? portalText("operations", "a_campaign_is_calling_this_list_editing_it_changes_who_the_55cd6bc1")
            : portalText("operations", "campaigns_are_calling_this_list_editing_it_changes_who_the_52942cee"),
        calling: true,
      };
    case "ready":
      return {
        label: portalText("operations", "not_in_a_campaign_0c35921f"),
        tip:
          row.campaignCount === 0
            ? portalText("operations", "checked_and_ready_to_call_every_row_has_a_valid_number_no_76796916")
            : portalText("operations", "checked_and_ready_to_call_every_row_has_a_valid_number_the_41111cc3"),
        calling: false,
      };
    case "syncing":
      return { label: portalText("operations", "syncing_4ae6fa22"), tip: portalText("operations", "the_list_is_being_brought_up_to_date_from_where_it_comes_f_600c0783"), calling: true };
    case "failed":
      return { label: portalText("operations", "failed_09fef5d8"), tip: portalText("operations", "the_list_could_not_be_read_open_it_to_see_why_874de2fa"), calling: false };
  }
}

/**
 * "3 numbers ready to save." / "2 ready · 1 to fix. Lines to fix are left out if you save." — the
 * paste check, from the API's own reading of what was pasted.
 */
export function pasteSummaryText(readableCount: number, toFixCount: number, shown: number): string {
  if (toFixCount === 0) return portalText("operations", "value_value_ready_to_save_5fc4e224", { v0: String(countText(readableCount)), v1: String(plural(readableCount, portalText("operations", "number_53b0a1b2"), portalText("operations", "numbers_138c2f84"))) });
  return portalText("operations", "value_ready_value_to_fixvalue_lines_to_fix_are_left_out_if_008ced0b", { v0: String(countText(readableCount)), v1: String(countText(toFixCount)), v2: String(toFixCount > shown ? portalText("operations", "first_value_shown_ad0af214", { v0: String(String(shown)) }) : "") });
}
